import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './SniperTradeHistory.css';

const API_BASE_URL = import.meta.env.VITE_API_URL ;

/**
 * SniperTradeHistory - Componente para mostrar historial de trades del Sniper
 */
const SniperTradeHistory = ({ userId, currentUser }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'exitTime', direction: 'desc' });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 15,
    skip: 0,
    hasMore: false
  });

  const fetchTrades = async () => {
    if (!userId) return;
    
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` }
      };
      
      const params = new URLSearchParams({
        limit: 100, // Aumentar para tener todos los datos para sort/search del lado del cliente
        skip: 0
      });
      
      // Si userId es diferente al usuario logueado, agregarlo como query param
      if (userId) {
        params.append('userId', userId);
      }
      
      const res = await axios.get(
        `${API_BASE_URL}/sniper/history?${params.toString()}`,
        config
      );
      
      if (res.data.success) {
        setTrades(res.data.data.trades || []);
        setPagination(prev => ({
          ...prev,
          total: res.data.data.pagination?.total || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching sniper history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchTrades();
    const interval = setInterval(fetchTrades, 30000);
    return () => clearInterval(interval);
  }, [userId]);

  const formatDate = (dateString) => {
    if (!dateString) return { time: 'N/A', day: 'N/A' };
    const date = new Date(dateString);
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    const day = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return { time, day };
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getReasonShort = (reason) => {
    if (!reason) return 'N/A';
    const map = {
      'TP1_TAKEN': 'TP1',
      'TP2_TAKEN': 'TP2',
      'STOP_LOSS': 'SL',
      'SL_TRIGGERED': 'SL',
      'SOLD_MANUAL': 'Manual',
      'VENTA MANUAL': 'Manual'
    };
    return map[reason.toUpperCase()] || reason.slice(0, 10);
  };

  const isAdmin = currentUser?.tipo === 'Admin';

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtrado y ordenado
  const filteredAndSortedTrades = useMemo(() => {
    let filtered = [...trades];
    
    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = filtered.filter(trade => 
        trade.tokenSymbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.tokenName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.tokenAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trade.reason?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Ordenar
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Casos especiales para fechas
      if (sortConfig.key === 'entryTime' || sortConfig.key === 'exitTime') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      // Para tokenName/tokenSymbol, manejar valores null/undefined
      if (sortConfig.key === 'tokenSymbol' || sortConfig.key === 'tokenName') {
        aVal = aVal || '';
        bVal = bVal || '';
        // Convertir a minúsculas para comparación case-insensitive
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      
      // Comparación
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [trades, searchTerm, sortConfig]);

  // Paginación del lado del cliente
  const currentPageTrades = useMemo(() => {
    const start = pagination.skip;
    const end = start + pagination.limit;
    return filteredAndSortedTrades.slice(start, end);
  }, [filteredAndSortedTrades, pagination.skip, pagination.limit]);

  // Calcular totales
  const totals = useMemo(() => {
    const all = filteredAndSortedTrades.reduce((acc, trade) => {
      acc.totalPnL += trade.pnlUsd || 0;
      acc.totalInvested += trade.investedUsd || 0;
      if (trade.pnlUsd > 0) acc.wins++;
      else if (trade.pnlUsd < 0) acc.losses++;
      return acc;
    }, { totalPnL: 0, totalInvested: 0, wins: 0, losses: 0 });
    
    const page = currentPageTrades.reduce((acc, trade) => {
      acc.totalPnL += trade.pnlUsd || 0;
      return acc;
    }, { totalPnL: 0 });
    
    return { all, page };
  }, [filteredAndSortedTrades, currentPageTrades]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading && trades.length === 0) {
    return (
      <div className="sniper-trade-history">
        <div className="trade-header">
          <h3 className="trade-title">📜 Trade History</h3>
        </div>
        <div className="trade-body">
          <div className="skeleton" style={{ height: '300px' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="sniper-trade-history">
      {/* Header con totales generales */}
      <div className="trade-header">
        <div className="header-left">
          <h3 className="trade-title">📜 Trade History</h3>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by token, address, or reason..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="clear-search"
          >
            ✕
          </button>
        )}
      </div>
      
      <div className="trade-body">
        {filteredAndSortedTrades.length === 0 ? (
          <div className="no-trades">
            <p>No trades found</p>
            <span>{searchTerm ? 'Try a different search term' : 'Trade history will appear here once you close positions'}</span>
          </div>
        ) : (
          <>
            <div className="trade-table-container">
              <table className="trade-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('tokenName')} className="sortable">
                      Token {getSortIcon('tokenName')}
                    </th>
                    <th onClick={() => handleSort('whaleOriginName')} className="sortable">
                      Whale {getSortIcon('whaleOriginName')}
                    </th>
                    <th onClick={() => handleSort('entryTime')} className="sortable">
                      Entry Time {getSortIcon('entryTime')}
                    </th>
                    <th onClick={() => handleSort('exitTime')} className="sortable">
                      Exit Time {getSortIcon('exitTime')}
                    </th>
                    <th onClick={() => handleSort('entryPrice')} className="sortable text-right">
                      Entry Price {getSortIcon('entryPrice')}
                    </th>
                    <th onClick={() => handleSort('exitPrice')} className="sortable text-right">
                      Exit Price {getSortIcon('exitPrice')}
                    </th>
                    <th onClick={() => handleSort('tokenAmount')} className="sortable text-right">
                      Amount {getSortIcon('tokenAmount')}
                    </th>
                    <th onClick={() => handleSort('pnlPercent')} className="sortable text-right">
                      PnL % {getSortIcon('pnlPercent')}
                    </th>
                    <th onClick={() => handleSort('pnlUsd')} className="sortable text-right">
                      PnL USD {getSortIcon('pnlUsd')}
                    </th>
                    {isAdmin && (
                      <th onClick={() => handleSort('reason')} className="sortable">
                        Reason {getSortIcon('reason')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {currentPageTrades.map((trade, idx) => {
                    const entryDate = formatDate(trade.entryTime);
                    const exitDate = formatDate(trade.exitTime);
                    const isProfit = trade.pnlUsd >= 0;

                    return (
                      <tr key={idx} className="trade-row">
                        <td className="trade-token">
                          <div className="token-cell">
                            <a
                              href={`https://photon-sol.tinyastro.io/en/lp/${trade.tokenAddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="token-link"
                              title={`View ${trade.tokenName || trade.tokenSymbol || 'token'} on Photon`}
                            >
                              <span className="token-symbol">
                                {trade.tokenName || trade.tokenSymbol || trade.tokenAddress?.slice(0, 8) + '...'}
                              </span>
                            </a>
                          </div>
                        </td>
                        <td className="trade-whale">
                          {trade.whaleOrigin ? (
                            <a
                              href={`https://solscan.io/account/${trade.whaleOrigin}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="whale-link"
                              title={`View ${trade.whaleOriginName || 'whale'} wallet on Solscan`}
                            >
                              🐋 {trade.whaleOriginName || trade.whaleOrigin.slice(0, 6)}
                            </a>
                          ) : (
                            <span className="no-whale">-</span>
                          )}
                        </td>
                        <td className="trade-time">
                          <div className="time-cell">
                            <span className="time">{entryDate.time}</span>
                            <span className="date">{entryDate.day}</span>
                          </div>
                        </td>
                        <td className="trade-time">
                          <div className="time-cell">
                            <span className="time">{exitDate.time}</span>
                            <span className="date">{exitDate.day}</span>
                          </div>
                        </td>
                        <td className="trade-price">
                          ${trade.entryPrice?.toFixed(6) || '0'}
                        </td>
                        <td className="trade-price">
                          ${trade.exitPrice?.toFixed(6) || '0'}
                        </td>
                        <td className="trade-amount">
                          {trade.tokenAmount?.toLocaleString('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          }) || '0'}
                        </td>
                        <td className={`trade-pnl ${isProfit ? 'profit' : 'loss'}`}>
                          {isProfit ? '+' : ''}{trade.pnlPercent?.toFixed(2) || '0'}%
                        </td>
                        <td className={`trade-pnl-usd ${isProfit ? 'profit' : 'loss'}`}>
                          {formatCurrency(trade.pnlUsd)}
                        </td>
                        {isAdmin && (
                          <td className="trade-reason">
                            <span className={`reason-badge ${trade.reason?.toLowerCase().replace(/\s+/g, '-')}`}>
                              {getReasonShort(trade.reason)}
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Totales de página y paginación */}
            <div className="table-footer">
              <div className="page-totals">
                <span className="label">Page Total:</span>
                <span className={`value ${totals.page.totalPnL >= 0 ? 'profit' : 'loss'}`}>
                  {formatCurrency(totals.page.totalPnL)}
                </span>
              </div>
              
              {/* Pagination */}
              {filteredAndSortedTrades.length > pagination.limit && (
                <div className="pagination">
                  <button
                    onClick={() => setPagination(prev => ({ 
                      ...prev, 
                      skip: Math.max(0, prev.skip - prev.limit) 
                    }))}
                    disabled={pagination.skip === 0}
                    className="btn-pagination"
                  >
                    ← Previous
                  </button>
                  <span className="pagination-info">
                    {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, filteredAndSortedTrades.length)} of {filteredAndSortedTrades.length}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ 
                      ...prev, 
                      skip: prev.skip + prev.limit 
                    }))}
                    disabled={pagination.skip + pagination.limit >= filteredAndSortedTrades.length}
                    className="btn-pagination"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SniperTradeHistory;
